import { Controller } from "@hotwired/stimulus";
import Cleave from "cleave.js";
import "cleave.js/dist/addons/cleave-phone.us";

// Connects to data-controller="checkout"
export default class extends Controller {
  static targets = [
    // Shipping Address
    "localPickupSwitch",
    "shippingAddress",
    "storeAddress",
    // Credit Card
    "cardName",
    "cardNumber",
    "cvc",
    "expDate",
    // Billing Address
    "useShippingAsBillingSwitch",
    "billingAddress",
    // Tax Exemption
    "taxExemptionSection",
    "taxExemptionCheckbox",
    "taxExemptionAlert",
    // Order summary targets
    "subtotal",
    "convenienceFee",
    "shippingCost",
    "taxAmount",
    "totalAmount",
    "shippingState",
  ];

  static values = {
    clientKey: String,
    apiLoginId: String,
    acceptjsUrl: String,
    taxExempt: Boolean,
  };

  connect() {
    this.hideOverlay();
    this.ensureAcceptJsLoaded();
    this.disableSubmitButton();
    this.submitting = false;

    // Credit Card listeners
    if (this.hasCardNumberTarget) {
      this.cardNumberCleave = new Cleave(this.cardNumberTarget, {
        creditCard: true,
      });
      this.cardNumberTarget.addEventListener("blur", this.validateCardNumber);
      this.cardNumberTarget.addEventListener("change", this.validateCardNumber);
    }
    if (this.hasExpDateTarget) {
      this.expDateCleave = new Cleave(this.expDateTarget, {
        date: true,
        datePattern: ["m", "y"],
      });
      this.expDateTarget.addEventListener("blur", this.validateExpDate);
      this.expDateTarget.addEventListener("change", this.validateExpDate);
    }
    if (this.hasCvcTarget) {
      this.cvcTarget.addEventListener("blur", this.validateCvc);
      this.cvcTarget.addEventListener("change", this.validateCvc);
    }
    if (this.hasCardNameTarget) {
      this.cardNameTarget.addEventListener("blur", this.validateCardName);
      this.cardNameTarget.addEventListener("change", this.validateCardName);
    }

    // Add listeners for all required fields (shipping, billing, etc.)
    this.requiredFields.forEach((input) => {
      input.addEventListener("blur", this.validateSingleField);
      input.addEventListener("change", this.validateSingleField);
    });

    this.element.addEventListener("submit", this.handleSubmit);

    // adjust UI based on local pickup switch
    this.togglePickup();

    // Tax exemption checkbox validation
    if (this.hasTaxExemptionCheckboxTarget) {
      this.taxExemptionCheckboxTarget.addEventListener("change", this.handleTaxExemptionChange);
    }

    // Update tax exemption section visibility based on shipping state
    // setTimeout(0) defers execution until after DOM updates from connect() complete
    setTimeout(() => {
      this.updateTaxExemptionVisibility();
      // If checkbox is pre-checked, show alert and update summary
      if (this.hasTaxExemptionCheckboxTarget && this.taxExemptionCheckboxTarget.checked) {
        this.toggleTaxExemptionAlert();
      }
      // Always update summary on page load to ensure tax is calculated correctly
      this.updateSummary();
    }, 0);
  }

  showOverlay() {
    const overlay = document.getElementById("payment-overlay");
    if (overlay) {
      overlay.classList.remove("d-none");
      document.body.classList.add("no-freeze-spinner");
    }
  }

  hideOverlay() {
    const overlay = document.getElementById("payment-overlay");
    if (overlay) {
      overlay.classList.add("d-none");
      document.body.classList.remove("no-freeze-spinner");
    }
  }

  showError(message) {
    let errorDiv = document.getElementById("payment-errors");
    if (!errorDiv) {
      errorDiv = document.createElement("div");
      errorDiv.id = "payment-errors";
      errorDiv.className = "alert alert-danger mt-3";
      errorDiv.setAttribute("role", "alert");
      const form = this.element.querySelector("form");
      if (form) {
        form.insertBefore(errorDiv, form.firstChild);
      } else {
        this.element.appendChild(errorDiv);
      }
    }
    errorDiv.textContent = message;
    errorDiv.classList.remove("d-none");
    errorDiv.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  ensureAcceptJsLoaded() {
    const url = this.acceptjsUrlValue;
    if (
      typeof window.Accept === "undefined" &&
      !document.getElementById("acceptjs-script")
    ) {
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.src = url;
      script.id = "acceptjs-script";
      script.onload = () => {
        this.enableSubmitButton();
      };
      script.onerror = () => {
        const error = new Error("Failed to load Accept.js checkout script");
        console.error("[checkout]", error, {
          controller: "checkout",
          action: "ensureAcceptJsLoaded",
          scriptUrl: url,
        });
      };
      document.head.appendChild(script);
    } else if (typeof window.Accept !== "undefined") {
      this.enableSubmitButton();
    }
  }

  disableSubmitButton() {
    const btn = this.element.querySelector('button[type="submit"]');
    if (btn) btn.disabled = true;
  }

  enableSubmitButton() {
    const btn = this.element.querySelector('button[type="submit"]');
    if (btn) btn.disabled = false;
  }

  handleSubmit = (e) => {
    e.preventDefault();

    let acceptTimeout = null;

    try {
      if (this.submitting) {
        // Prevent double submit
        return;
      }
      this.submitting = true;
      this.disableSubmitButton();

      if (typeof window.Accept === "undefined") {
        // Accept.js not loaded yet
        this.submitting = false;
        this.disableSubmitButton();

        const error = new Error("Accept.js not loaded when checkout submitted");
        console.error("[checkout]", error, {
          controller: "checkout",
          action: "handleSubmit",
        });

        let errorDiv = document.getElementById("payment-errors");
        this.showError("Payment system is still loading. Please wait a moment and try again.");
        this.ensureAcceptJsLoaded();
        return;
      }

      this.showOverlay();

      // Validate all required fields (shipping, billing, etc.)
      const allFieldsValid = this.validateRequiredFields();
      // Credit card validation
      const validCardNumber = this.validateCardNumber();
      const validExpDate = this.validateExpDate();
      const validCvc = this.validateCvc();
      const validCardName = this.validateCardName();

      if (
        !(
          allFieldsValid &&
          validCardNumber &&
          validExpDate &&
          validCvc &&
          validCardName
        )
      ) {
        // Focus first invalid field
        const firstInvalid = this.element.querySelector(".is-invalid");
        if (firstInvalid) firstInvalid.focus();

        this.submitting = false;
        this.enableSubmitButton();
        this.hideOverlay();

        return;
      }

      // 2. Prepare data for Accept.js
      const cardNumber = this.cardNumberTarget.value.replace(/\s+/g, "");
      const exp = this.expDateTarget.value.split("/");
      const expMonth = exp[0].trim();
      let expYear = exp[1].trim();
      if (expYear.length === 2) expYear = "20" + expYear;
      const cardCode = this.cvcTarget.value.trim();

      // 3. Accept.js credentials
      const authData = {
        clientKey: this.clientKeyValue,
        apiLoginID: this.apiLoginIdValue,
      };

      const cardData = {
        cardNumber: cardNumber,
        month: expMonth,
        year: expYear,
        cardCode: cardCode,
      };

      const secureData = {
        authData: authData,
        cardData: cardData,
      };

      acceptTimeout = setTimeout(() => {
        this.submitting = false;
        this.enableSubmitButton();
        this.hideOverlay();
        this.showError("Payment processing timed out. Please try again.");
        console.error("[checkout] Accept.js timeout - callback not called within 30 seconds", {
          controller: "checkout",
          action: "handleSubmit",
        });
      }, 30000);

      Accept.dispatchData(secureData, (response) => {
          clearTimeout(acceptTimeout);

          try {
            if (response.messages.resultCode === "Ok") {
              // Fill hidden fields with opaqueData
              const opaqueData = response.opaqueData;
              document.getElementById("checkout_opaque_data_descriptor").value =
                opaqueData.dataDescriptor;
              document.getElementById("checkout_opaque_data_value").value =
                opaqueData.dataValue;
              // Submit the form
              const form = this.element.querySelector("form");
              if (form) {
                form.submit();
              } else {
                const error = new Error("No form found to submit after Accept.js success");
                console.error("[checkout]", error, {
                  controller: "checkout",
                  action: "acceptjs_callback",
                });
                console.error("No form found to submit!");
              }
            } else {
              this.submitting = false;
              this.enableSubmitButton();
              this.hideOverlay();

              // Show Accept.js error
              const errorMsg = response.messages.message[0].text;

              const error = new Error(`Accept.js checkout error: ${errorMsg}`);
              console.error("[checkout]", error, {
                controller: "checkout",
                resultCode: response.messages.resultCode,
                messageCode: response.messages.message[0].code,
              });

              this.showError(errorMsg);
            }
          } catch (error) {
            this.submitting = false;
            this.enableSubmitButton();
            this.hideOverlay();

            console.error("[checkout]", error, {
              controller: "checkout",
              action: "acceptjs_callback",
            });

            console.error("Error in Accept.js callback:", error);

            this.showError("An error occurred processing your payment. Please try again.");
          }
        });
    } catch (error) {
      if (acceptTimeout) {
        clearTimeout(acceptTimeout);
      }
      this.submitting = false;
      this.enableSubmitButton();
      this.hideOverlay();

      console.error("[checkout]", error, {
        controller: "checkout",
        action: "handleSubmit",
      });

      this.showError("An error occurred. Please try again.");
    }
  };

  async updateSummary() {
    this.updateTaxExemptionVisibility();

    // Show loading indicator in summary fields
    const loading =
      "<span " +
      'class="spinner-border spinner-border-sm text-muted" ' +
      'role="status" aria-hidden="true"></span>';
    this.convenienceFeeTargets.forEach((el) => (el.innerHTML = loading));
    this.shippingCostTargets.forEach((el) => (el.innerHTML = loading));
    this.taxAmountTargets.forEach((el) => (el.innerHTML = loading));
    this.totalAmountTargets.forEach((el) => (el.innerHTML = loading));

    // Get order ID from data attribute
    const orderId =
      document.getElementById("checkout-container")?.dataset.orderId;
    if (!orderId) {
      console.error("[checkout] No order ID found in #checkout-container");
      return;
    }

    // Gather data from form fields
    // Check for tax exemption checkbox or hidden field (for verified accounts)
    const taxExemptionCheckbox = this.hasTaxExemptionCheckboxTarget
      ? this.taxExemptionCheckboxTarget
      : null;
    const taxExemptionHidden = document.querySelector(
      'input[name="checkout[customer_claimed_tax_exemption]"][type="hidden"]'
    );
    const taxExemptionClaimed =
      (taxExemptionCheckbox && taxExemptionCheckbox.checked) ||
      (taxExemptionHidden && taxExemptionHidden.value === "true");

    const data = {
      local_pickup:
        this.localPickupSwitchTarget && this.localPickupSwitchTarget.checked,
      shipping_state: this.hasShippingStateTarget
        ? this.shippingStateTarget.value
        : undefined,
      customer_claimed_tax_exemption: taxExemptionClaimed,
    };

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
      if (!csrfToken) {
        throw new Error("CSRF token not found");
      }

      const response = await fetch(`/orders/${orderId}/checkout/summary_json`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = new Error(`Failed to update checkout summary: ${response.status}`);
        error.status = response.status;
        throw error;
      }

      const summaryData = await response.json();

      if (!summaryData || typeof summaryData !== "object") {
        throw new Error("Invalid response format from summary endpoint");
      }

      if (summaryData.convenience_fee) {
        this.convenienceFeeTargets.forEach(
          (el) => (el.innerHTML = summaryData.convenience_fee),
        );
      }
      if (summaryData.shipping_cost) {
        this.shippingCostTargets.forEach((el) => {
          if (summaryData.shipping_cost === "FREE") {
            el.innerHTML =
              '<strong class="d-block mb-1 text-success">FREE</strong>';
          } else {
            el.innerHTML = summaryData.shipping_cost;
          }
        });
      }
      if (summaryData.tax_amount) {
        this.taxAmountTargets.forEach((el) => {
          if (summaryData.tax_amount === "EXEMPT") {
            el.innerHTML = '<strong class="text-success">EXEMPT</strong>';
          } else {
            el.innerHTML = summaryData.tax_amount;
          }
        });
      }
      if (summaryData.total_amount) {
        this.totalAmountTargets.forEach(
          (el) => (el.innerHTML = summaryData.total_amount),
        );
      }
    } catch (error) {
      console.error("[checkout] Error updating summary:", error, {
        controller: "checkout",
        action: "updateSummary",
        orderId,
      });
    }
  }

  validateCardNumber = () => {
    if (!this.hasCardNumberTarget) return true;
    const value = this.cardNumberTarget.value.replace(/\s+/g, "");
    let valid = value.length >= 13 && value.length <= 19 && /^\d+$/.test(value);
    this.setFieldValidity(
      this.cardNumberTarget,
      "card-number-feedback",
      valid,
      valid ? "" : "Card number is invalid.",
    );
    return valid;
  };

  validateExpDate = () => {
    if (!this.hasExpDateTarget) return true;
    const value = this.expDateTarget.value.trim();
    const match = value.match(/^(\d{2})\/(\d{2,4})$/);
    let valid = false;
    let msg = "";
    if (!match) {
      msg = "Expiration date format is invalid.";
    } else {
      let [_, mm, yy] = match;
      mm = parseInt(mm, 10);
      yy = parseInt(yy, 10);
      if (yy < 100) {
        yy += 2000;
      }
      const now = new Date();
      const thisMonth = now.getMonth() + 1;
      const thisYear = now.getFullYear();
      if (
        mm < 1 ||
        mm > 12 ||
        yy < thisYear ||
        (yy === thisYear && mm < thisMonth)
      ) {
        msg = "Expiration date is in the past.";
      } else {
        valid = true;
      }
    }
    this.setFieldValidity(this.expDateTarget, "exp-date-feedback", valid, msg);
    return valid;
  };

  validateCvc = () => {
    if (!this.hasCvcTarget) return true;
    const value = this.cvcTarget.value.trim();
    let valid = /^\d{3,4}$/.test(value);
    this.setFieldValidity(
      this.cvcTarget,
      "cvc-feedback",
      valid,
      valid ? "" : "CVV is invalid.",
    );
    return valid;
  };

  validateCardName = () => {
    if (!this.hasCardNameTarget) return true;
    const value = this.cardNameTarget.value.trim();
    let valid = value.length > 0;
    this.setFieldValidity(
      this.cardNameTarget,
      "card-name-feedback",
      valid,
      valid ? "" : "Name on Card is required.",
    );
    return valid;
  };

  setFieldValidity(input, feedbackId, valid, msg) {
    let feedback = document.getElementById(feedbackId);
    if (!feedback) {
      // Dynamically create the feedback div if it doesn't exist
      feedback = document.createElement("div");
      feedback.id = feedbackId;
      feedback.className = "invalid-feedback";
      // Insert after the input
      if (input.nextSibling) {
        input.parentNode.insertBefore(feedback, input.nextSibling);
      } else {
        input.parentNode.appendChild(feedback);
      }
    }
    if (input && feedback) {
      if (!valid) {
        input.classList.add("is-invalid");
        feedback.textContent = msg;
      } else {
        input.classList.remove("is-invalid");
        feedback.textContent = "";
      }
    }
  }

  togglePickup() {
    // setTimeout(0) defers DOM updates until after checkbox state change is processed
    setTimeout(() => {
      const isPickup = this.localPickupSwitchTarget.checked;
      this.shippingAddressTarget.style.display = isPickup ? "none" : "";
      this.storeAddressTarget.style.display = isPickup ? "" : "none";

      // Disable/enable all inputs and selects in shipping address
      this.shippingAddressTarget
        .querySelectorAll("input, select")
        .forEach((el) => {
          el.disabled = isPickup;
          el.classList.remove("is-invalid");
        });

      if (isPickup) {
        this.useShippingAsBillingSwitchTarget.checked = false;
        this.toggleBilling();
      }
      this.updateTaxExemptionVisibility();

      this.useShippingAsBillingSwitchTarget.disabled = isPickup;

      // Always update summary after toggling pickup
      this.updateSummary();
    }, 0);
  }

  toggleBilling() {
    // setTimeout(0) defers DOM updates until after checkbox state change is processed
    setTimeout(() => {
      const isSameAsShipping = this.useShippingAsBillingSwitchTarget.checked;
      this.billingAddressTarget.style.display = isSameAsShipping ? "none" : "";
      // Disable/enable all inputs and selects in billing address
      this.billingAddressTarget
        .querySelectorAll("input, select")
        .forEach((el) => {
          el.disabled = isSameAsShipping;
          el.classList.remove("is-invalid");
        });
    }, 0);
  }

  // Helper to get all required fields in the form
  get requiredFields() {
    return this.element.querySelectorAll("input[required], select[required]");
  }

  // Validate all required fields (shipping, billing, etc.)
  validateRequiredFields = () => {
    let allValid = true;
    this.requiredFields.forEach((input) => {
      if (input.disabled) return; // Skip disabled fields
      const valid = input.value.trim().length > 0;
      const label =
        input.labels && input.labels.length > 0
          ? input.labels[0].textContent
          : "This field";
      this.setFieldValidity(
        input,
        `${input.id}-feedback`,
        valid,
        valid ? "" : `${label} is required.`,
      );
      if (!valid) allValid = false;
    });
    return allValid;
  };

  // Validate a single field (for blur/change)
  validateSingleField = (event) => {
    const input = event.target;
    if (input.disabled) return true; // Skip disabled fields
    const valid = input.value.trim().length > 0;
    const label =
      input.labels && input.labels.length > 0
        ? input.labels[0].textContent
        : "This field";
    this.setFieldValidity(
      input,
      `${input.id}-feedback`,
      valid,
      valid ? "" : `${label} is required.`,
    );
    return valid;
  };

  handleTaxExemptionChange = () => {
    if (this.hasTaxExemptionCheckboxTarget) {
      this.toggleTaxExemptionAlert();
      this.updateSummary();
    }
  };

  toggleTaxExemptionAlert() {
    if (this.hasTaxExemptionAlertTarget) {
      if (this.hasTaxExemptionCheckboxTarget && this.taxExemptionCheckboxTarget.checked) {
        this.taxExemptionAlertTarget.style.display = "";
      } else {
        this.taxExemptionAlertTarget.style.display = "none";
      }
    }
  }


  updateTaxExemptionVisibility() {
    // setTimeout(0) defers execution to ensure shipping state value is updated in DOM
    setTimeout(() => {
      if (!this.hasTaxExemptionSectionTarget) {
        return;
      }

      // Don't show section if account is tax exempt (section is not rendered)
      if (this.hasTaxExemptValue && this.taxExemptValue === true) {
        return;
      }

      const isPickup = this.localPickupSwitchTarget && this.localPickupSwitchTarget.checked;
      
      if (isPickup) {
        this.showTaxExemptionSection();
        return;
      }

      if (!this.hasShippingStateTarget) {
        this.hideTaxExemptionSection();
        return;
      }

      const shippingState = this.shippingStateTarget.value;
      const stateValue = shippingState ? shippingState.trim().toUpperCase() : "";
      
      if (stateValue === "FL") {
        this.showTaxExemptionSection();
      } else {
        this.hideTaxExemptionSection();
      }
    }, 0);
  }

  showTaxExemptionSection() {
    if (this.hasTaxExemptionSectionTarget) {
      this.taxExemptionSectionTarget.style.display = "";
    }
  }

  hideTaxExemptionSection() {
    if (this.hasTaxExemptionSectionTarget) {
      this.taxExemptionSectionTarget.style.display = "none";
      // Uncheck checkbox when hiding
      if (this.hasTaxExemptionCheckboxTarget) {
        this.taxExemptionCheckboxTarget.checked = false;
      }
      // Hide alert when section is hidden
      if (this.hasTaxExemptionAlertTarget) {
        this.taxExemptionAlertTarget.style.display = "none";
      }
    }
  }

  disconnect() {
    if (this.cardNumberCleave) {
      this.cardNumberCleave.destroy();
      if (this.hasCardNumberTarget) {
        this.cardNumberTarget.removeEventListener("blur", this.validateCardNumber);
        this.cardNumberTarget.removeEventListener("change", this.validateCardNumber);
      }
    }
    if (this.expDateCleave) {
      this.expDateCleave.destroy();
      if (this.hasExpDateTarget) {
        this.expDateTarget.removeEventListener("blur", this.validateExpDate);
        this.expDateTarget.removeEventListener("change", this.validateExpDate);
      }
    }
    if (this.hasCvcTarget) {
      this.cvcTarget.removeEventListener("blur", this.validateCvc);
      this.cvcTarget.removeEventListener("change", this.validateCvc);
    }
    if (this.hasCardNameTarget) {
      this.cardNameTarget.removeEventListener("blur", this.validateCardName);
      this.cardNameTarget.removeEventListener("change", this.validateCardName);
    }

    this.requiredFields.forEach((input) => {
      input.removeEventListener("blur", this.validateSingleField);
      input.removeEventListener("change", this.validateSingleField);
    });

    this.element.removeEventListener("submit", this.handleSubmit);

    if (this.hasTaxExemptionCheckboxTarget) {
      this.taxExemptionCheckboxTarget.removeEventListener("change", this.handleTaxExemptionChange);
    }
  }
}
